"use client";

import React, {JSX, ReactNode, useLayoutEffect, useRef, useState} from "react";
import type {HTMLInputAutoCompleteAttribute} from "react";

import {useSnapshot} from "valtio";

import {useScrollIntoView} from "@/shared/hooks/dom/use-scroll-into-view";
import {useTranslation} from "@/shared/i18n";
import {type FieldInstance} from "@/shared/models/form/fields";

import {type FormFieldSize} from "./types";

import "./styles/text-field.scss";

// Map type to inputMode for mobile keyboard hints
const typeToInputMode: Record<string, React.HTMLAttributes<HTMLInputElement>["inputMode"]> = {
    email: "email",
    tel: "tel",
    url: "url",
    search: "search",
    number: "numeric",
};

interface TextFieldProps {
    model: FieldInstance;
    id?: string;
    label?: string;
    labelVariant?: "default" | "subtle";
    type?: string;
    placeholder?: string;
    hidePlaceholderOnFocus?: boolean;
    fontSize?: "default" | "small" | "flexible";
    fontWeight?: "default" | "medium";
    height?: "default" | "small" | "flexible";
    className?: string;
    size?: FormFieldSize;
    icon?: ReactNode; // Optional icon element (SVG, FontAwesome, etc.)
    iconPosition?: "left" | "right"; // Icon position
    action?: ReactNode; // Optional action element (button, etc.)
    autoComplete?: HTMLInputAutoCompleteAttribute;
    autoCorrect?: HTMLInputAutoCompleteAttribute;
    autoCapitalize?: HTMLInputAutoCompleteAttribute;
    name?: string;
}

export function TextField({
    model,
    id,
    label,
    labelVariant = "default",
    type = "text",
    placeholder,
    hidePlaceholderOnFocus = false,
    fontSize = "default",
    fontWeight = "default",
    height = "default",
    className,
    size,
    icon,
    iconPosition = "left",
    autoCorrect = "on",
    autoCapitalize = "on",
    action,
    autoComplete = "on",
    name,
}: TextFieldProps): JSX.Element {
    const {t} = useTranslation("validation");
    const snapshot = useSnapshot(model);
    const scrollRef = useScrollIntoView<HTMLInputElement>(model);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);

    // Types that don't support setSelectionRange - convert to "text" with inputMode
    const unsupportedTypes = ["email", "number"];
    const actualType = unsupportedTypes.includes(type) ? "text" : type;

    // Types that support setSelectionRange
    const supportsSelection = ["text", "search", "url", "tel", "password"].includes(actualType);

    // Combine refs
    const setRefs = (element: HTMLInputElement | null): void => {
        (scrollRef as React.MutableRefObject<HTMLInputElement | null>).current = element;
        inputRef.current = element;
    };

    // Restore cursor position after render (from model)
    useLayoutEffect(() => {
        if (!inputRef.current) return;
        if (!snapshot.cursorPosition) return;
        if (!supportsSelection) return;

        const {start, end} = snapshot.cursorPosition;
        inputRef.current.setSelectionRange(start, end);
        model.clearCursorPosition();
    }, [snapshot.value, snapshot.cursorPosition, supportsSelection, model]);

    // Derive inputMode from original type (for mobile keyboard hints)
    const inputMode = typeToInputMode[type];

    const hasExtras = icon || action;
    const hasLeftIcon = icon && iconPosition === "left";
    const sizeClass = size ? `input-field--${size}` : "";
    const labelClass = labelVariant === "subtle" ? "input-field__label--subtle" : "";
    const fontSizeClass =
        fontSize === "small" ? "input-field__input--font-small" : fontSize === "flexible" ? "input-field__input--font-flexible" : "";
    const fontWeightClass = fontWeight === "medium" ? "input-field__input--font-medium" : "";
    const heightClass = height === "flexible" ? "input-field__input--height-flexible" : "";
    const wrapperHeightClass =
        height === "small" ? "input-field__wrapper--height-small" : height === "flexible" ? "input-field__wrapper--height-flexible" : "";
    const inputPaddingClass = !hasLeftIcon ? "input-field__input--no-left-icon" : "";
    return (
        <div className={`input-field ${hasExtras ? "input-field--with-extras" : ""} ${sizeClass} ${className || ""}`}>
            {label && (
                <label htmlFor={id} className={`input-field__label ${labelClass}`}>
                    {label} {model.isRequired && <span className="input-field__required">*</span>}
                </label>
            )}

            <div className={`input-field__wrapper ${wrapperHeightClass}`}>
                {icon && iconPosition === "left" && <div className="input-field__icon input-field__icon--left">{icon}</div>}

                <input
                    ref={setRefs}
                    id={id}
                    autoCorrect={autoCorrect}
                    autoCapitalize={autoCapitalize}
                    name={name}
                    type={actualType}
                    inputMode={inputMode}
                    value={snapshot.value}
                    onChange={(e) => {
                        // Save cursor position before setValue (for restoration after transform)
                        if (supportsSelection) {
                            model.setCursorPosition(e.target.selectionStart ?? 0, e.target.selectionEnd ?? 0);
                        }
                        model.setValue(e.target.value);
                    }}
                    onBlur={() => {
                        setIsFocused(false);
                        model.onBlur();
                    }}
                    onFocus={() => {
                        setIsFocused(true);
                        model.onFocus();
                    }}
                    disabled={snapshot.disabled}
                    placeholder={hidePlaceholderOnFocus && isFocused ? "   " : placeholder}
                    autoComplete={autoComplete}
                    className={`input-field__input ${fontSizeClass} ${fontWeightClass} ${heightClass} ${inputPaddingClass} ${snapshot.shouldShowError ? "has-error" : ""}`}
                />

                {icon && iconPosition === "right" && <div className="input-field__icon input-field__icon--right">{icon}</div>}

                {action && <div className="input-field__action">{action}</div>}
            </div>

            {snapshot.shouldShowError && snapshot.error && (
                <span className="input-field__error-text">{t(snapshot.error.message as Parameters<typeof t>[0], snapshot.error.messageParams)}</span>
            )}
        </div>
    );
}
